import axios from 'axios'

const BASE_URL = import.meta.env.VITE_API_URL || '/api'

const api = axios.create({
  baseURL: BASE_URL,
  timeout: 15000,
  headers: { 'Content-Type': 'application/json' },
})

// ── Token helpers ────────────────────────────────────────────
const TOKEN_KEY = 'tonys-auth'

function getToken() {
  try {
    const stored = localStorage.getItem(TOKEN_KEY)
    if (!stored) return null
    return JSON.parse(stored)?.state?.token || null
  } catch {
    return null
  }
}

// ── Interceptors ─────────────────────────────────────────────
api.interceptors.request.use(config => {
  const token = getToken()
  if (token) config.headers.Authorization = `Bearer ${token}`
  return config
})

api.interceptors.response.use(
  res => res.data,
  err => {
    if (err.response?.status === 401) {
      window.dispatchEvent(new Event('auth:logout'))
    }
    const msg = err.response?.data?.detail || err.message || 'Request failed'
    return Promise.reject(new Error(typeof msg === 'string' ? msg : JSON.stringify(msg)))
  }
)

// ── Auth ─────────────────────────────────────────────────────
export const authService = {
  login: (username, password) =>
    api.post('/login', new URLSearchParams({ username, password }), {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    }),
  login2fa: (username, password, totp_code) =>
    api.post('/login/2fa', { username, password, totp_code }),
  // Email OTP 2FA login steps
  loginEmailOtpSend: (username, password) =>
    api.post('/login/email-2fa/send', { username, password }),
  loginEmailOtpVerify: (username, password, otp_code) =>
    api.post('/login/email-2fa/verify', { username, password, otp_code }),

  register: (data) => api.post('/register', data),

  me: () => api.get('/me'),

  customerMe: () => api.get('/customer/me'),

  // ── 2FA management ────────────────────────────────────────
  get2faStatus:    () => api.get('/user/2fa/status'),
  setup2fa:        () => api.post('/user/2fa/setup'),
  verify2fa:       (code) => api.post('/user/2fa/verify', { code }),
  disable2fa:      () => api.delete('/user/2fa'),
  enableEmailOtp:  () => api.post('/user/email-2fa/enable'),
  disableEmailOtp: () => api.post('/user/email-2fa/disable'),
}

// ── Products ─────────────────────────────────────────────────
export const productService = {
  getAll: (params = {}) => api.get('/products/priced', { params }),

  getById: (id) => api.get(`/products/${id}`),

  getImages: (id) => api.get(`/products/${id}/images`),

  getBrands: () => api.get('/products/brands'),

  search: (query, params = {}) =>
    api.get('/products/priced', { params: { search: query, ...params } }),

  getByCategory: (categoryId, params = {}) =>
    api.get('/products/priced', { params: { category_id: categoryId, ...params } }),

  getFeatured: (limit = 12) =>
    api.get('/products/priced', { params: { limit, sort: 'newest' } }),
}

// ── Categories ───────────────────────────────────────────────
export const categoryService = {
  getAll: () => api.get('/categories'),
  getById: (id) => api.get(`/categories/${id}`),
}

// ── Cart ─────────────────────────────────────────────────────
export const cartService = {
  get: () => api.get('/cart'),
  getTotal: () => api.get('/cart/total'),
  addItem: (productId, quantity = 1) =>
    api.post('/cart/items', { product_id: productId, quantity }),
  updateItem: (cartItemId, quantity) =>
    api.put(`/cart/items/${cartItemId}`, { quantity }),
  removeItem: (cartItemId) => api.delete(`/cart/items/${cartItemId}`),
  clear: () => api.delete('/cart/clear'),
  checkout: (data) => api.post('/cart/checkout/place-order', data),
}

// ── Orders ────────────────────────────────────────────────────
export const orderService = {
  getMine: () => api.get('/orders/me'),
  getById: (id) => api.get(`/orders/me/${id}`),
  create: (data) => api.post('/orders/', data),
  updateStatus: (id, status) => api.post(`/orders/${id}/status`, { status }),
  reorder: (id) => api.post(`/orders/${id}/reorder`),
}

// ── Wishlist ──────────────────────────────────────────────────
export const wishlistService = {
  get:             ()              => api.get('/wishlist'),
  add:             (productId)     => api.post('/wishlist/items', { product_id: productId }),
  remove:          (wishlistItemId) => api.delete(`/wishlist/items/${wishlistItemId}`),
  removeByProduct: (productId)     => api.delete(`/wishlist/by-product/${productId}`),
}

// ── Reviews ───────────────────────────────────────────────────
export const reviewService = {
  getForProduct: (productId) => api.get(`/products/${productId}/reviews`),
  create: (data) => api.post('/reviews/', data),
  delete: (reviewId) => api.delete(`/reviews/${reviewId}`),
}

// ── Addresses ─────────────────────────────────────────────────
export const addressService = {
  getAll: () => api.get('/addresses'),
  create: (data) => api.post('/addresses', data),
  update: (id, data) => api.put(`/addresses/${id}`, data),
  delete: (id) => api.delete(`/addresses/${id}`),
}

// ── Templates ─────────────────────────────────────────────────
export const templateService = {
  getAll:     ()              => api.get('/templates'),
  getById:    (id)            => api.get(`/templates/${id}`),
  create:     (data)          => api.post('/templates', data),
  update:     (id, data)      => api.put(`/templates/${id}`, data),
  saveItems:  (id, items)     => api.put(`/templates/${id}/items`, items),
  delete:     (id)            => api.delete(`/templates/${id}`),
  loadToCart: (id)            => api.post(`/templates/${id}/load-to-cart`),
  fromOrder:  (orderId, data) => api.post(`/templates/from-order/${orderId}`, data),
}

// ── Promotions / Notifications ────────────────────────────────
export const promotionService = {
  // Customer-facing: get promos/notifications sent to this customer
  getAll: () => api.get('/promotions'),
  markRead: (id) => api.post(`/promotions/${id}/read`),
  dismiss: (id) => api.post(`/promotions/${id}/dismiss`),
}

export const notificationService = {
  requestOtp: (phone) => api.post('/otp/request', { phone }),
  verifyOtp: (phone, otp) => api.post('/otp/verify', { phone, otp }),
}

// ── Public Settings (unauthenticated) ─────────────────────────────
export const publicSettingsService = {
  get: () => api.get('/public/settings'),
}

export default api
